import type { GalleryModel, FileModel } from '@/models/gallery.model'
import { GalleryError } from '@/services/gallery-error'
import { getMockGallery } from '@/services/mock-gallery.service'
import { shouldUseMock } from '@/services/gallery-data-source.service'
import { getLogger } from '@/logger'
import { getGalleryApiUrl } from '@/env'
import { t } from '@/i18n/translate-utils'

/** Raw file shape returned by the BFF `/shared-files` endpoint. */
interface ShareFileDto {
  fileName: string
  mediaType: string
  fileSize: number
  presignedUrl: string
}

/**
 * Raw gallery response returned by the BFF `/shared-files/:shortId` and
 * `/shared-files/:shortId/preview` endpoints.
 */
interface ShareContentResponse {
  files: ShareFileDto[]
  /** Gallery title for recipient display — required by contract. */
  title: string
  /** Sender display name — required by contract. */
  senderName: string
  /** ISO 8601 expiry date string. */
  expiryDate: string
  /** ISO 8601 opened-at timestamp; present after first open. Ignored in this phase. */
  openedAt?: string
}

/** Options accepted by {@link fetchGallery}. */
export interface FetchGalleryOptions {
  /**
   * When `true`, the BFF preview endpoint (`/shared-files/:shortId/preview`)
   * is called instead of the normal endpoint.  Defaults to `false`.
   *
   * In mock mode this flag is ignored and the same mock dataset is returned
   * for both normal and preview routes.
   */
  preview?: boolean
}

function mapShareFileToFile(dto: ShareFileDto, index: number): FileModel {
  return {
    id: `file-${index}`,
    name: dto.fileName,
    contentType: dto.mediaType,
    size: dto.fileSize,
    contentUrl: dto.presignedUrl,
    thumbnailUrl: dto.mediaType?.startsWith('image/') ? dto.presignedUrl : '',
  }
}

function mapShareContentToGallery(shortId: string, raw: ShareContentResponse): GalleryModel {
  const fileCount = raw.files.length

  return {
    id: shortId,
    title:
      raw.title?.trim() ||
      (fileCount === 1
        ? t('gallery.fallback.filesCount_singular', '{{fileCount}} file', { fileCount })
        : t('gallery.fallback.filesCount_plural', '{{fileCount}} files', { fileCount })),
    senderName: raw.senderName?.trim() || t('gallery.fallback.unknownSender', 'Unknown'),
    expiresAt: raw.expiryDate ? new Date(raw.expiryDate) : null,
    files: raw.files.map(mapShareFileToFile),
  }
}

type LogContext = { shortId: string; preview: boolean; source: 'bff' }

function buildLogContext(shortId: string, preview: boolean): LogContext {
  return { shortId, preview, source: 'bff' }
}

function buildGalleryUrl(shortId: string, preview: boolean): string {
  const base = getGalleryApiUrl()
  return preview ? `${base}/${shortId}/preview` : `${base}/${shortId}`
}

function handleHttpError(response: Response, context: LogContext): never {
  const { status } = response
  if (status === 400) {
    getLogger().warn('[gallery-data] invalid shortId', { ...context, status, failureClass: 'http' })
    throw new GalleryError('not-found')
  }
  if (status === 404) {
    getLogger().warn('[gallery-data] share not found', { ...context, status, failureClass: 'http' })
    throw new GalleryError('not-found')
  }
  if (status === 410) {
    getLogger().warn('[gallery-data] share expired', { ...context, status, failureClass: 'http' })
    throw new GalleryError('expired')
  }
  getLogger().error('[gallery-data] unexpected HTTP error', { ...context, status, failureClass: 'http' })
  throw new GalleryError('error')
}

function assertJsonContentType(response: Response, context: LogContext): void {
  const contentType = response.headers.get('content-type')
  if (!contentType?.includes('application/json')) {
    getLogger().error('[gallery-data] non-JSON response', {
      ...context,
      status: response.status,
      contentType,
      failureClass: 'non-json',
    })
    throw new GalleryError('error')
  }
}

async function fetchFromBff(shortId: string, preview: boolean): Promise<GalleryModel> {
  const context = buildLogContext(shortId, preview)
  const url = buildGalleryUrl(shortId, preview)

  try {
    const response = await fetch(url)

    if (!response.ok) {
      handleHttpError(response, context)
    }

    assertJsonContentType(response, context)

    const raw = (await response.json()) as ShareContentResponse

    return mapShareContentToGallery(shortId, raw)
  } catch (err) {
    if (err instanceof GalleryError) {
      throw err
    }
    getLogger().error('[gallery-data] unexpected failure', { ...context, err })
    throw new GalleryError('error')
  }
}

/**
 * Fetches a gallery by short ID from the Gallery BFF.
 *
 * **Source resolution** — delegates to {@link shouldUseMock} (see
 * `gallery-data-source.service`):
 * - Mock source: returns the static {@link getMockGallery} dataset.  The
 *   `preview` flag does not change the mock payload (normal and preview use
 *   the same dataset).
 * - BFF source: calls `GET /shared-files/:shortId` or
 *   `GET /shared-files/:shortId/preview` and maps the response.
 *
 * **BFF error mapping:**
 * - **400** → `GalleryError('not-found')` (invalid shortId logged)
 * - **404** → `GalleryError('not-found')` (logged)
 * - **410** → `GalleryError('expired')` (logged)
 * - **200 with missing/empty `title`** → normalized to `<count> file(s)`
 * - **200 with missing/empty `senderName`** → normalized to `Unknown`
 * - **non-JSON 200** → `GalleryError('error')` (logged)
 * - **other 4xx / 5xx** → `GalleryError('error')` (logged)
 * - **network failure** → `GalleryError('error')` (logged)
 *
 * Failure logs include structured context: `shortId`, `preview`, source mode
 * (`mock|bff`), HTTP status when available, and failure class.
 *
 * @param shortId - The gallery short ID extracted from the URL path.
 * @param options - Optional fetch options including the `preview` flag.
 * @returns A resolved {@link GalleryModel}.
 * @throws {@link GalleryError} on any error condition.
 */
export async function fetchGallery(shortId: string, options?: FetchGalleryOptions): Promise<GalleryModel> {
  const preview = options?.preview ?? false

  if (shouldUseMock()) {
    return getMockGallery()
  }

  return fetchFromBff(shortId, preview)
}
